/**
 * Single source of truth for the member "role" — the "What brings you here?"
 * answer collected at signup ([signup/page.tsx]) and editable later in account
 * settings ([account/page.tsx]).
 *
 * The role is stored in two places that must agree:
 *   • `auth.users.user_metadata.role` — written at signup, the cheap read
 *     source every client surface uses (no DB round-trip, available on the
 *     `user` object from `useAuth()`).
 *   • `public.profiles.role` — mirrored from metadata by the
 *     `on_auth_user_created` trigger; the editable copy the account settings
 *     page reads.
 *
 * Because the two can drift (e.g. an OAuth signup never sets metadata, or a
 * settings edit writes only the profile), every write path in this app routes
 * through helpers here so they stay in lockstep:
 *   - signup email  → metadata set inline by `signUp({ data })`
 *   - signup OAuth  → stashed pre-redirect, applied by `applyPendingProfile`
 *   - settings edit → `account/page.tsx` updates metadata alongside the profile
 *
 * The role exists to *do work*: the signup screen promises it "helps us show
 * you the right tools first," so the empty state, the post-verify next-steps,
 * and the dashboard quick-actions all tailor themselves via the maps below.
 */

import type { SupabaseClient, User } from "@supabase/supabase-js";

export type UserRole = "homebuyer" | "broker" | "developer" | "planner" | "other";

/** The canonical select options — imported by both signup and account so the
 *  label wording lives in exactly one place. */
export const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: "homebuyer", label: "Buying a home" },
  { value: "broker", label: "Broker or advisor" },
  { value: "developer", label: "Developer or investor" },
  { value: "planner", label: "Government or urban planner" },
  { value: "other", label: "Something else" },
];

export const ROLE_LABELS: Record<string, string> = Object.fromEntries(
  ROLE_OPTIONS.map(o => [o.value, o.label]),
);

const KNOWN_ROLES = new Set<string>(ROLE_OPTIONS.map(o => o.value));

/** sessionStorage keys for the OAuth stash-and-apply dance. */
export const PENDING_ROLE_KEY = "ps_pending_role";
export const PENDING_MARKETING_KEY = "ps_pending_marketing";

/**
 * Resolve the member's role from their auth user, or `null` when it isn't set
 * (legacy accounts, or an OAuth signup whose `applyPendingProfile` hasn't run
 * yet). Callers treat `null` as "no personalization — show the neutral copy."
 */
export function resolveRole(user: User | null | undefined): UserRole | null {
  const raw = user?.user_metadata?.role;
  return typeof raw === "string" && KNOWN_ROLES.has(raw) ? (raw as UserRole) : null;
}

/* ── Per-surface copy ──────────────────────────────────────────────────────
 * Keyed by role; every map carries an explicit fallback so a `null` role (or a
 * future role value we don't recognize) renders the neutral, buyer-leaning
 * default rather than throwing. */

/** The noun in the empty-state headline "Score your first ___." */
export const ROLE_FIRST_PLOT_NOUN: Record<UserRole, string> = {
  homebuyer: "home",
  broker: "listing",
  developer: "site",
  planner: "area",
  other: "plot",
};

export function firstPlotNoun(role: UserRole | null): string {
  return role ? ROLE_FIRST_PLOT_NOUN[role] : "plot";
}

/** The three "What's next" bullets on the post-verification screen. Each trio
 *  ends on the plan that matches the role, since plans map onto roles
 *  (buyer → Pass, broker → Pro, developer/planner → Studio). */
const NEXT_STEPS_DEFAULT = [
  "Score your first three properties — free, no card.",
  "Download a PDF report to share.",
  "The Buyer's Pass unlocks 30 days of unlimited scoring.",
];

const ROLE_NEXT_STEPS: Record<UserRole, string[]> = {
  homebuyer: [
    "Score the homes you're weighing — three free, no card.",
    "Download a PDF to talk it over with your partner.",
    "The Buyer's Pass unlocks 30 days of unlimited scoring for €39.",
  ],
  broker: [
    "Score any property for a client — three free to start.",
    "Hand them a clean, client-ready PDF report.",
    "Pro adds folders, tags, and shareable client links for €49/mo.",
  ],
  developer: [
    "Assess a site's zoning, risk, and upside — three free.",
    "Export the underlying data straight into your own model.",
    // Only what ships: Site Finder and the City Atlas. Bulk scoring and the
    // REST API are roadmap rows on /pricing (tierFeatures.ts) and must not be
    // promised here as what €199 buys today.
    "Studio unlocks Site Finder and the City Atlas for €199/mo.",
  ],
  planner: [
    "Score any site or address — three free, no card.",
    "See PlotScore across a whole neighbourhood, not one plot at a time.",
    "Studio unlocks the city-wide PlotScore atlas for €199/mo.",
  ],
  other: NEXT_STEPS_DEFAULT,
};

export function nextSteps(role: UserRole | null): string[] {
  return role ? ROLE_NEXT_STEPS[role] : NEXT_STEPS_DEFAULT;
}

/**
 * Dashboard hero personalization — the loud, on-arrival moment. Most users
 * land here straight after confirming their email, so this is where the role
 * has to visibly pay off. The `tag` deliberately echoes the exact signup
 * option label ("Buying a home", etc.) so the member recognizes their own
 * choice reflected back; `line` tailors the greeting and `cta` relabels the
 * map button. `null`/"other" → neutral (no tag, default button).
 */
const ROLE_HERO: Record<UserRole, { line: string; cta: string }> = {
  homebuyer: { line: "Let's find the home that scores.", cta: "Score a home" },
  broker:    { line: "Client-ready intelligence, on demand.", cta: "Score a listing" },
  developer: { line: "Assess a site's upside in seconds.", cta: "Assess a site" },
  planner:   { line: "Read a whole area at a glance.", cta: "Open the map" },
  other:     { line: "", cta: "Open the map" },
};

export function dashboardHero(
  role: UserRole | null,
): { tag: string | null; line: string; cta: string } {
  if (!role || role === "other") return { tag: null, line: "", cta: "Open the map" };
  return { tag: ROLE_LABELS[role], line: ROLE_HERO[role].line, cta: ROLE_HERO[role].cta };
}

/**
 * OAuth role capture. `signInWithOAuth` redirects away before we can write
 * `user_metadata`, so the signup page stashes the chosen role + marketing
 * consent in sessionStorage (survives the same-tab round-trip to the provider
 * and back), and this runs once a session exists — wired into AuthContext so
 * it fires on every authenticated page load.
 *
 * Best-effort and self-clearing: the stash is removed synchronously on read so
 * concurrent auth events can't double-write, and an existing role is never
 * overwritten (so it's a no-op for email signups, which already set metadata).
 */
export async function applyPendingProfile(
  supabase: SupabaseClient,
  user: User,
): Promise<void> {
  let pendingRole: string | null = null;
  let pendingMarketing: string | null = null;
  try {
    pendingRole = sessionStorage.getItem(PENDING_ROLE_KEY);
    pendingMarketing = sessionStorage.getItem(PENDING_MARKETING_KEY);
    // Clear synchronously, before any await, so a second auth event firing
    // mid-write reads null and bails — no duplicate updateUser calls.
    sessionStorage.removeItem(PENDING_ROLE_KEY);
    sessionStorage.removeItem(PENDING_MARKETING_KEY);
  } catch {
    // Storage disabled (private mode) — nothing to apply.
    return;
  }

  if (!pendingRole || !KNOWN_ROLES.has(pendingRole)) return;
  // Never clobber a role the user already has (e.g. they later re-auth via
  // Google having originally signed up by email).
  if (resolveRole(user)) return;

  const marketingConsent = pendingMarketing !== "false";

  try {
    // user_metadata is the read source every surface uses…
    await supabase.auth.updateUser({
      data: { role: pendingRole, marketing_consent: marketingConsent },
    });
    // …and the profiles row is the editable mirror the account page reads.
    // The trigger created this row at signup before metadata existed, so we
    // upsert to bring it in line. RLS restricts this to the user's own row.
    await supabase
      .from("profiles")
      .upsert(
        { id: user.id, role: pendingRole, marketing_consent: marketingConsent },
        { onConflict: "id" },
      );
  } catch {
    // Best-effort: if the write fails the user simply sees neutral copy until
    // they set a role in settings. Not worth interrupting the sign-in for.
  }
}
